import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import { TextField, Box, Stack, Typography, Alert } from '@mui/material';
import { FormContainer } from '../../forms/FormContainer';
import { ModalConfirm } from '../../../molecules/modal';
import { useCreateLegalEntity } from 'src/use-cases/settings/useLegalEntityInfiniteList';
import { openToast, openToastError } from 'src/use-cases/toast/useToast';

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: '100%',
  '& .MuiInputBase-root': {
    fontSize: '16px',
    backgroundColor: '#ffffff',
    [theme.breakpoints.down(1520)]: {
      fontSize: '14px',
    },
  },
  '& .MuiInputBase-input': {
    padding: '10px 14px',
  },
  '& .MuiFormLabel-root': {
    fontSize: '16px',
    [theme.breakpoints.down(1520)]: {
      fontSize: '14px',
    },
  },
}));

interface ICreateEntityModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  onSuccess?: (values: any) => void;
  title: string;
  addressType: 'PICK_UP' | 'DELIVERY';
}

export const CreateEntityModal = ({ open, setOpen, onSuccess, title, addressType }: ICreateEntityModalProps) => {
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateLegalEntity();

  const formContext = useForm({
    defaultValues: {
      name: '',
      code: '',
      address: '',
      country: '',
      city: '',
      state: '',
      zipcode: '',
    },
  });

  const { control, handleSubmit, reset } = formContext;

  const onSubmit = async (values) => {
    try {
      setError(null);
      const hasAddress = !!(values.address || values.country || values.city || values.state || values.zipcode);
      const payload: any = {
        name: values.name,
        code: values.code,
      };
      if (hasAddress) {
        payload.addresses = [
          {
            address: values.address,
            country: values.country,
            city: values.city,
            state: values.state,
            zipcode: values.zipcode,
            type: addressType,
            active: true,
          },
        ];
      }
      const response = await createMutation.mutateAsync(payload);
      openToast({ toastType: 'success', title: 'Success', description: `${title.replace('Add New ', '')} created successfully` });
      onSuccess?.(response?.data);
      reset();
      setOpen(false);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || `Failed to create ${title}`;
      setError(message);
      openToastError(new Error(message));
    }
  };

  return (
    <FormContainer formContext={formContext}>
      <ModalConfirm
        title={title}
        open={open}
        onAccept={() => {
          handleSubmit(onSubmit)();
        }}
        onCancel={() => {
          setError(null);
          reset();
          setOpen(false);
        }}
        width={680}
        buttonAcceptLabel={createMutation.isLoading ? 'Creating...' : 'Create'}
        buttonCancelLabel='Cancel'
      >
        <Box sx={{ mt: '1rem', minHeight: '350px' }}>
          {error && (
            <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {/* Name Information */}
          <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 2, fontSize: '14px' }}>
            Name Information
          </Typography>
          <Stack direction='row' spacing={2} sx={{ mb: 4 }}>
            <Controller
              name='name'
              control={control}
              rules={{ required: true }}
              render={({ field }) => <StyledTextField {...field} placeholder='Name' fullWidth variant='outlined' />}
            />
            <Controller
              name='code'
              control={control}
              rules={{ required: true }}
              render={({ field }) => <StyledTextField {...field} placeholder='Code' sx={{ maxWidth: '30%' }} variant='outlined' />}
            />
          </Stack>

          {/* Address Information */}
          <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 2, fontSize: '14px' }}>
            Address Information
          </Typography>
          <Stack spacing={2}>
            <Controller
              name='address'
              control={control}
              render={({ field }) => <StyledTextField {...field} placeholder='Address' fullWidth variant='outlined' />}
            />
            <Stack direction='row' spacing={2}>
              <Controller
                name='country'
                control={control}
                render={({ field }) => <StyledTextField {...field} placeholder='Country' fullWidth variant='outlined' />}
              />
              <Controller
                name='city'
                control={control}
                render={({ field }) => <StyledTextField {...field} placeholder='City' fullWidth variant='outlined' />}
              />
            </Stack>
            <Stack direction='row' spacing={2}>
              <Controller
                name='state'
                control={control}
                render={({ field }) => <StyledTextField {...field} placeholder='State' fullWidth variant='outlined' />}
              />
              <Controller
                name='zipcode'
                control={control}
                render={({ field }) => <StyledTextField {...field} placeholder='Zipcode' fullWidth variant='outlined' />}
              />
            </Stack>
          </Stack>
        </Box>
      </ModalConfirm>
    </FormContainer>
  );
};

/** @deprecated Use CreateEntityModal with title="Add New Consignee" instead */
export const CreateConsigneeModal = (props: Omit<ICreateEntityModalProps, 'title'>) => (
  <CreateEntityModal {...props} title='Add New Consignee' addressType='DELIVERY' />
);
